import { useEffect } from 'react';

import { useParams } from 'react-router';

import { Order_By, useAddEngagementRecordMutation, useGetFilteredClientRecordsQuery } from '@/gql/operations';
import { useCurrentUserMpId } from '@/shared/hooks';

import useParseRecords from '../hooks/useParseRecords';

/**
 * Fetches a single client record based on the :clientId URL param.
 * Reuses the existing GetFilteredClientRecords query with a ClientNumber filter.
 * Automatically creates an engagement record if one doesn't exist.
 */
export const useClientRecord = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const currentUserMpId = useCurrentUserMpId();

  const { data, loading, error, refetch } = useGetFilteredClientRecordsQuery({
    variables: {
      where: { ClientNumber: { _eq: clientId } },
      limit: 1,
      offset: 0,
      order_by: [{ ClientName1: Order_By.Asc }],
    },
    skip: !clientId,
  });

  const [addEngagementRecord, { loading: addingEngagement }] = useAddEngagementRecordMutation();

  // Auto-create engagement record if it doesn't exist
  useEffect(() => {
    const createEngagementRecordIfNeeded = async () => {
      if (!clientId || !currentUserMpId || loading || addingEngagement) {
        return;
      }

      const clientRecord = data?.mlfsl_client?.[0];
      const hasEngagementRecord = clientRecord?.cmap_clm_engagement_record?.id;

      if (clientRecord && !hasEngagementRecord) {
        try {
          await addEngagementRecord({
            variables: {
              client_number: clientId,
              created_by: currentUserMpId,
              updated_by: currentUserMpId,
              type: 'EL',
            },
          });
          await refetch();
        } catch (err) {
          console.error('Failed to create engagement record:', err);
        }
      }
    };

    void createEngagementRecordIfNeeded();
  }, [clientId, currentUserMpId, data, loading, addingEngagement, addEngagementRecord, refetch]);

  const parsedRecords = useParseRecords(data?.mlfsl_client || []);
  const record = parsedRecords[0] ?? null;

  return {
    record,
    isLoading: loading || addingEngagement,
    error: error || null,
  };
};
